/**
 * Empty-scan copy — Ori, 19 Jul 2026. SHIP VERBATIM.
 *
 * Deliberately a sibling of `wsCopy.js`, not an addition to it. Different
 * register: `ws.*` manages alarm, `safety_net` sells a next action, this file
 * only EXPLAINS. An empty scan is a legitimate result — nothing here is amber,
 * nothing apologises, nothing offers a fix where there isn't one.
 *
 * Each entry is { d } or { d, m }: desktop and (where it differs) mobile.
 *
 * BUILD RULES (spec "Build notes" — these are requirements, not style):
 *   1. Branch on `empty_reason`, NEVER on the top-level `all_seen` flag.
 *      `all_seen` is `fetched > 0 && new == 0`, which is ALSO true for
 *      `all_filtered`. That flag is exactly why the old toast told people
 *      "No new signals since your last scan" for scans that found hundreds of
 *      posts and set every one aside. Wiring these strings to it preserves the
 *      bug behind nicer words.
 *   2. Numbers for `all_seen` and `all_filtered` ONLY. In those two the count is
 *      proof of work done. In `no_matches` the only true number is zero, and
 *      printing zero just makes the emptiness louder.
 *   3. `all_seen` at n === 1: drop the count, use the no-count variant.
 *   4. "checked", not "seen" — load-bearing. A post is in the ledger because it
 *      was ANALYSED, which includes posts analysed and dropped as junk that the
 *      user never laid eyes on. "You've already seen these" would be false for
 *      exactly those. Do not let this be softened back to "seen".
 */

export const EMPTY_COPY = {
  all_seen: {
    // "up to date", not "no new leads": names a state you're IN, not a thing you
    // didn't get. Fires constantly under multi-brand.
    headline: { d: "You're up to date" },
    support: {
      d: (n) =>
        `We went through ${n} posts and we'd already checked every one for this brand. Nothing new has come up since your last scan.`,
      m: (n) => `We'd already checked all ${n} posts we found. Nothing new since your last scan.`,
    },
    supportNoCount: {
      d: "Every post we found, we'd already checked for this brand. Nothing new has come up since your last scan.",
      m: "We'd already checked everything we found. Nothing new since your last scan.",
    },
    next: { d: "Nothing to do here — your next scan will pick up anything new." },
  },

  all_filtered: {
    headline: { d: "Nothing worth your time in this one" },
    // The number does the most work here: "We read 340 posts and none were
    // customers" is a feature statement; without it the same fact reads as
    // failure.
    support: {
      d: (n) =>
        `We read ${n} posts and set every one aside — job ads, spam, and people talking about something else. None of them were someone looking for what you do.`,
      m: (n) => `We read ${n} posts. None were people looking for what you do.`,
    },
    // "Nothing to fix" pre-empts the owner's assumption that an empty screen is
    // their fault.
    next: {
      d: "Nothing to fix. If a few scans in a row come back like this, it's worth trying a different keyword or two.",
      m: "Nothing to fix. Worth trying a new keyword if this keeps up.",
    },
  },

  no_matches: {
    headline: { d: "Nothing came back this time" },
    // Second desktop sentence is the anti-blame move: it puts the mismatch on
    // the SEARCH, not on the owner's keywords or their market.
    support: {
      d: "We searched where your customers usually talk and didn't find any posts to look at. Sometimes the words people actually type just don't line up with the ones we're searching for.",
      m: "We searched where your customers talk and found no posts this time.",
    },
    // "changes what we go looking for", not "usually works": we have no evidence
    // for a hit rate, and inventing small confidence is what Law #8 exists to stop.
    next: {
      d: "Widening your search area, or adding a phrase your customers might type, changes what we go looking for.",
      m: "Try a wider search area, or add a phrase your customers might type.",
    },
  },

  /**
   * One appended line under any of the three reasons above — never its own card.
   *
   * Deliberately does NOT apologise (that confirms something went wrong TO
   * them), does NOT name a way to get the credit back (there isn't one), and
   * does NOT say "come back tomorrow" — the cap is IST-day-keyed, so that would
   * be true for Indian users, hours off for US/AU, and reads as an instruction
   * to farm free scans. Silence about the reset is the honest option.
   */
  charged: {
    // {n} = `empty_scan_refunds_per_day` from the poll endpoint. Policy reads
    // better than a vague "a few" — but the vague form is the honest fallback
    // when the number genuinely isn't in the payload. Never guess a number here.
    d: (n) =>
      `This one used a scan — we cover ${coverPhrase(n)}, and today's are used up.`,
    m: "This one used a scan. Today's cover for empty scans is used up.",
  },
  refunded: { d: "Nothing charged for this one." },
};

/** "3 empty scans a day" / "1 empty scan a day" / "a few empty scans a day". */
function coverPhrase(n) {
  if (typeof n !== "number" || !Number.isFinite(n) || n < 1) return "a few empty scans a day";
  return n === 1 ? "1 empty scan a day" : `${n} empty scans a day`;
}

/** Pick the desktop or mobile variant. `v` is a copy node; `n` is any arg. */
export function pick(node, isMobile, n) {
  if (!node) return null;
  const raw = isMobile && node.m !== undefined ? node.m : node.d;
  return typeof raw === "function" ? raw(n) : raw;
}

/**
 * Resolve the full set of lines for one empty scan.
 *
 * @param {object}  o
 * @param {string}  o.reason           `empty_reason` — the ONLY thing we branch on.
 * @param {number}  o.count            `empty_count`.
 * @param {boolean} o.refunded         `action_refunded`.
 * @param {string}  o.refundDeclined   `refund_declined_reason` (set ⇒ charged).
 * @param {number}  o.refundsPerDay    `empty_scan_refunds_per_day`.
 * @param {boolean} o.isMobile
 * @returns {{headline, support, next, money}|null} null for an unknown/absent reason —
 *          we render nothing rather than a confident default. A scan document written
 *          before this field existed has no honest explanation available, and inventing
 *          one is worse than the silence it replaces.
 */
export function resolveEmptyCopy({
  reason,
  count,
  refunded,
  refundDeclined,
  refundsPerDay,
  isMobile,
}) {
  const node = EMPTY_COPY[reason];
  if (!node) return null;

  let support;
  if (reason === "all_seen") {
    // n === 1 → "all 1 posts" is broken English and "1 post" over-narrates a
    // single item. Ori's no-count variant is the specified answer.
    const n = Number(count);
    support =
      Number.isFinite(n) && n > 1
        ? pick(node.support, isMobile, n)
        : pick(node.supportNoCount, isMobile);
  } else if (reason === "all_filtered") {
    const n = Number(count);
    // The count IS the copy here — there is no no-count variant written. If the
    // number is missing or zero the sentence would be false, so drop the whole
    // support line rather than print "We read 0 posts".
    support = Number.isFinite(n) && n > 0 ? pick(node.support, isMobile, n) : null;
  } else {
    support = pick(node.support, isMobile);
  }

  // Every empty scan attempts a refund server-side (the refund path fires on
  // `fetched == 0 || all_seen`, and all three reasons satisfy one of those), so
  // exactly one of these two lines is true. If neither flag is present we say
  // nothing about money at all rather than assume.
  let money = null;
  if (refundDeclined) money = pick(EMPTY_COPY.charged, isMobile, refundsPerDay);
  else if (refunded) money = pick(EMPTY_COPY.refunded, isMobile);

  return {
    headline: pick(node.headline, isMobile),
    support,
    next: pick(node.next, isMobile),
    money,
    charged: !!refundDeclined,
  };
}
